import { cx } from "@/utils/cx";

type TabHeaderProps = {
  title: string;
  subtitle: string;
  hasChanges: boolean;
  isPending: boolean;
  onSave: () => void;
  onCancel: () => void;
  rightContent?: React.ReactNode;
  // Pin the header (title + save/cancel) to the top of the scrolling tab content.
  sticky?: boolean;
};

export const TabHeader = ({
  title,
  subtitle,
  hasChanges,
  isPending,
  onSave,
  onCancel,
  rightContent,
  sticky = false,
}: TabHeaderProps) => (
  <div
    className={cx(
      "flex flex-row justify-between items-start",
      sticky && "sticky top-0 z-20 bg-primary py-4 border-b border-secondary",
    )}
  >
    <div className="flex flex-col gap-1">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      <p className="text-sm text-tertiary">{subtitle}</p>
    </div>
    <div className="flex flex-row gap-3 items-center">
      {rightContent}
      <button
        type="button"
        onClick={onCancel}
        disabled={!hasChanges || isPending}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors border ${
          !hasChanges || isPending
            ? "bg-primary text-gray-400 border-disabled_subtle cursor-not-allowed"
            : "bg-primary text-secondary border-primary hover:bg-primary_hover"
        }`}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={!hasChanges || isPending}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          hasChanges && !isPending
            ? "bg-brand-solid text-white hover:bg-brand-solid_hover"
            : "bg-disabled text-gray-400 cursor-not-allowed"
        }`}
      >
        {isPending ? "Saving..." : "Save"}
      </button>
    </div>
  </div>
);

type TabHeaderReadOnlyProps = {
  title: string;
  subtitle: string;
  rightContent?: React.ReactNode;
};

export const TabHeaderReadOnly = ({ title, subtitle, rightContent }: TabHeaderReadOnlyProps) => (
  <div className="flex flex-row justify-between items-start">
    <div className="flex flex-col gap-1">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      <p className="text-sm text-tertiary">{subtitle}</p>
    </div>
    {rightContent}
  </div>
);
